import React, { useEffect } from "react";
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@lemonadetraining/web-ui";
import {
  RADAR_AXIS_DISPLAY_NAMES,
  type AthleteRadarProfile,
  type AxisDetail,
  type RadarAxis,
} from "@lemonadetraining/contracts";
import IndicatorProgressBar from "./IndicatorProgressBar";
import { defaultContextMessage } from "./axisDetailHelpers";
import EmptyState from "../empty-state/EmptyState";
import RadarExplainerPopover from "../radar/RadarExplainerPopover";

export interface AxisDetailViewProps {
  axis: RadarAxis;
  profile: AthleteRadarProfile | null;
  /**
   * Fully-loaded axis detail payload (movement indicators, benchmark label,
   * contextual message). Computed client-side from the profile + 1RM records
   * + movement registry by `computeAxisDetail`.
   * - `null` (or omitted) → registry/data not yet loaded → render a skeleton.
   * - present with empty `indicators` → axis legitimately has no logged
   *   contributors → render the per-axis empty state.
   */
  detail?: AxisDetail | null;
  /** Invoked when the user clicks the "Overview" breadcrumb segment. */
  onBack: () => void;
  /** Where the back link points without script, e.g. "?tab=overview". */
  backHref?: string;
}

const AxisDetailView: React.FC<AxisDetailViewProps> = ({
  axis,
  profile,
  detail,
  onBack,
  backHref = "/dashboard",
}) => {
  const displayName = detail?.axisDisplayName ?? RADAR_AXIS_DISPLAY_NAMES[axis];
  // Defensive optional chaining: `isAthleteRadarProfile` upstream doesn't yet
  // validate `axisLevels`/`scores`, so a malformed doc could reach here
  // without them.
  const score = Math.round(detail?.score ?? profile?.scores?.[axis] ?? 0);
  const level = profile?.axisLevels?.[axis] ?? 0;

  const isWeakest = profile?.weakestAxis === axis;
  const isStrongest = profile?.strongestAxis === axis;
  const contextMessage =
    detail?.contextMessage ?? defaultContextMessage(axis, isWeakest, isStrongest);
  const benchmarkLabel = detail?.benchmarkLabel;

  const handleBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Respect open-in-new-tab affordances on the breadcrumb's <a href>:
    // primary-button-only, no modifier keys. Otherwise let the browser handle.
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onBack();
  };
  const handleBackKey = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onBack();
    }
  };

  // Esc closes the detail view from anywhere on the page, unless it is closing
  // a dialog on top (Radix marks the key handled; the dialog is still mounted).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  return (
    <section
      data-testid="axis-detail-view"
      data-axis={axis}
      aria-label={`${displayName} axis detail`}
      className="flex flex-col gap-4"
    >
      <Breadcrumb data-testid="axis-detail-breadcrumb" aria-label="Breadcrumb">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              href={backHref}
              onClick={handleBackClick}
              onKeyDown={handleBackKey}
              data-testid="axis-detail-breadcrumb-back"
            >
              Overview
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage data-testid="axis-detail-breadcrumb-current">
              {displayName}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <CardTitle className="text-lg">
                {displayName}
                <Badge
                  variant="secondary"
                  className="ml-3 align-middle"
                  data-testid="axis-detail-score-badge"
                  aria-label={`Score ${score} of 100`}
                >
                  {score} / 100
                </Badge>
              </CardTitle>
              {profile ? <RadarExplainerPopover subject="self" /> : null}
            </div>
            {benchmarkLabel ? (
              <Badge variant="outline" className="text-xs" data-testid="axis-detail-benchmark">
                {benchmarkLabel}
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground" data-testid="axis-detail-context-message">
            {contextMessage}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="axis-detail-level">
            Level {level}
          </p>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Key indicators</CardTitle>
        </CardHeader>
        <CardContent>
          {detail == null ? (
            <div
              className="flex flex-col gap-3"
              data-testid="axis-detail-indicators-loading"
              role="status"
              aria-live="polite"
              aria-label="Loading key indicators"
            >
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : detail.indicators.length > 0 ? (
            <div className="flex flex-col gap-5" data-testid="axis-detail-indicators">
              {detail.indicators.map((indicator) => (
                <IndicatorProgressBar key={indicator.movementID} indicator={indicator} />
              ))}
            </div>
          ) : (
            <EmptyState
              message={`Log your first ${displayName} movement to see your full shape`}
              data-testid="axis-detail-indicators-empty"
            />
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AxisDetailView;
